// @flow
import React, { useState } from 'react';
import useIsomorphicLayoutEffect from '../../../utils/useIsomorphicLayoutEffect';
import styled, { keyframes, css } from 'styled-components';
import { maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Datalayer } from '@finect/front-gtm';
import { X } from '@finect/tabular-icons/x';
import { Button } from '@finect/tabular-core/Button';
import { getFont } from '@finect/tabular-utils/getFont';
import {
  isFinecter,
  useVersionNotes,
  parseVersionItem,
  renderText,
  shouldAnnounceVersion,
  rememberSeenVersion
} from '../../../scenes/Plus/VersionNotes/utils';

const show = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const animation = css`
  animation: ${show} 0.3s ease;
`;

const OverBkg = styled.div`
  position: fixed;
  z-index: 1000;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  ${animation};
`;

const ContentBox = styled.div`
  position: fixed;
  z-index: 1001;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
  background: ${({ theme }) => theme.plus?.background || '#0070f3'};
  color: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  ${animation};

  ${maxMediaQueries.phone`
    padding: 24px 16px;
    max-height: 80dvh;
  `}
`;

const CloseButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  transition: color 0.2s;

  &:hover {
    color: #fff;
  }
`;

const Badge = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 20px;
  ${getFont(-1)}
  font-weight: 600;
  margin-bottom: 16px;

  ${maxMediaQueries.phone`
    margin-bottom: 10px;
  `}
`;

const Title = styled.h2`
  ${getFont(4)}
  font-weight: 800;
  margin-bottom: 8px;

  ${maxMediaQueries.phone`
    ${getFont(3)}
    font-weight: 800;
  `}
`;

const Subtitle = styled.p`
  ${getFont(1)}
  opacity: 0.9;
  margin-bottom: 24px;

  ${maxMediaQueries.phone`
    ${getFont(0)}
    margin-bottom: 12px;
  `}
`;

const FeaturesList = styled.ul`
  text-align: left;
  margin: 0 0 32px 0;
  padding: 0;
  list-style: none;
  width: 100%;

  ${maxMediaQueries.phone`
    margin-bottom: 16px;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
  `}
`;

const FeatureItem = styled.li`
  position: relative;
  padding-left: 28px;
  margin-bottom: 12px;
  ${getFont(0)}

  ${maxMediaQueries.phone`
    margin-bottom: 8px;
    ${getFont(-1)}
  `}

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    font-weight: bold;
    color: #fff;
    font-size: ${({ $important }) => ($important ? '16px' : 'inherit')};
  }

  ${({ $important }) =>
    $important &&
    `
    font-weight: 700;
  `}

  ${({ $internal }) =>
    $internal &&
    `
    color: rgba(255, 255, 255, 0.7);
    font-style: italic;
    background: rgba(255, 255, 255, 0.1);
    padding: 0 6px 6px 24px;
    margin-bottom: 0;
    margin-left: -4px;
    
    &::after {
      content: ' (Interno)';
      font-size: 8px;
      text-transform: uppercase;
      opacity: 0.8;
    }
  `}
`;

const VersionNotesModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const user = useSelector(state => state.user.user);
  const { latestVersion } = useVersionNotes();
  const linkColor = '#fff';

  useIsomorphicLayoutEffect(() => {
    // Solo abre, nunca cierra: si el ajuste se recargara y trajera otra cosa, un setIsVisible(false)
    // aquí le cerraría el modal en la cara a quien lo está leyendo. Cerrarlo es cosa de handleClose.
    if (shouldAnnounceVersion(latestVersion)) setIsVisible(true);
  }, [latestVersion]);

  const handleClose = () => {
    if (latestVersion) rememberSeenVersion(latestVersion.version);
    setIsVisible(false);
  };

  if (!isVisible || !latestVersion) return null;

  return (
    <>
      <Datalayer
        id="Impression"
        type="fiEvent"
        params={{
          category: 'VersionModalImpression',
          action: latestVersion.version,
          label: 'plus-version-notes'
        }}
      />
      <OverBkg onClick={handleClose} />
      <ContentBox>
        <CloseButton onClick={handleClose}>
          <X size={24} />
        </CloseButton>
        <Badge>¡Novedades en Finect Plus!</Badge>
        <Title>{latestVersion.title}</Title>
        <Subtitle>Hemos añadido nuevas funcionalidades para mejorar tu experiencia.</Subtitle>

        <FeaturesList>
          {latestVersion.items.slice(0, 4).map((item, i) => {
            const { text, important } = parseVersionItem(item);

            return (
              <FeatureItem key={i} $important={important}>
                {renderText(text, linkColor)}
              </FeatureItem>
            );
          })}

          {isFinecter(user) &&
            latestVersion.internalItems &&
            latestVersion.internalItems.slice(0, 2).map((item, i) => {
              const { text, important } = parseVersionItem(item);

              return (
                <FeatureItem key={`internal-${i}`} $important={important} $internal>
                  {renderText(text, linkColor)}
                </FeatureItem>
              );
            })}
        </FeaturesList>

        <Button as={Link} to="/plus/novedades" secondary style={{ width: '100%' }} onClick={handleClose}>
          Ver todas las novedades
        </Button>
      </ContentBox>
    </>
  );
};

export default VersionNotesModal;
